import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavContainer from "../../NavContainer";
import Swal from "sweetalert2";
import WILAYAS from "../../wilayas";
import LoadingIcon from "../../components/Icons/LoadingIcon";
import CartContext from "../../CartContext";
import { axiosInstance } from "../../config";
import { masahifSizesWithProducts } from "../../data";

const MasahifPage = () => {
  const navigate = useNavigate();
  const { orderPlaced } = useContext(CartContext);
  const [quantity, setQuantity] = useState(1);
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [product, setProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryCharge, setDeliveryCharge] = useState(400);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    addressDetails: "",
    wilaya: "",
  });
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    wilaya: "",
    addressDetails: "",
  });

  // Set initial selected product ID based on first size and first product
  useEffect(() => {
    if (masahifSizesWithProducts[0][selectedSizeIndex]?.ids?.length > 0) {
      setSelectedProductId(
        masahifSizesWithProducts[0][selectedSizeIndex].ids[0].id
      );
    }
  }, []);

  // Fetch product data whenever selectedProductId changes
  useEffect(() => {
    const fetchProductById = async () => {
      if (!selectedProductId) return;

      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `/api/products/${selectedProductId}`
        );
        setProduct(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching product:", error);
        setLoading(false);
      }
    };

    fetchProductById();
  }, [selectedProductId]);

  // Update selected product when size changes
  useEffect(() => {
    if (masahifSizesWithProducts[0][selectedSizeIndex]?.ids?.length > 0) {
      setSelectedProductId(
        masahifSizesWithProducts[0][selectedSizeIndex].ids[0].id
      );
    }
  }, [selectedSizeIndex]);

  const handleQuantityChange = (e) => {
    if (e.target.value < 1) {
      setQuantity(1);
    } else {
      setQuantity(e.target.value);
    }
  };

  const deliveryPrice = (wilaya) => {
    for (let i = 0; i < WILAYAS.length; i++) {
      if (WILAYAS[i].name === wilaya) {
        return WILAYAS[i].price;
      }
    }
  };

  const handleWilayaChange = (e) => {
    setData((prev) => {
      return { ...prev, wilaya: e.target.value };
    });
    const deliveryPriced = deliveryPrice(e.target.value);
    setDeliveryCharge(deliveryPriced);
  };

  const calculateTotalPrice = () => {
    if (!product) return 0;
    return product.price * quantity + deliveryCharge;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset field errors
    setFieldErrors({
      firstName: "",
      lastName: "",
      phone: "",
      wilaya: "",
      addressDetails: "",
    });

    // Validate required fields
    let hasError = false;

    if (!data.firstName.trim()) {
      setFieldErrors((prev) => ({
        ...prev,
        firstName: "الرجاء إدخال الاسم الأول",
      }));
      hasError = true;
    }

    if (!data.lastName.trim()) {
      setFieldErrors((prev) => ({ ...prev, lastName: "الرجاء إدخال اللقب" }));
      hasError = true;
    }

    if (!data.phone.trim()) {
      setFieldErrors((prev) => ({ ...prev, phone: "الرجاء إدخال رقم الهاتف" }));
      hasError = true;
    }

    if (!data.wilaya) {
      setFieldErrors((prev) => ({ ...prev, wilaya: "الرجاء اختيار الولاية" }));
      hasError = true;
    }

    if (!data.addressDetails.trim()) {
      setFieldErrors((prev) => ({
        ...prev,
        addressDetails: "الرجاء إدخال الدائرة",
      }));
      hasError = true;
    }

    if (!selectedProductId) {
      setError("الرجاء اختيار المنتج");
      hasError = true;
    }

    if (hasError) {
      setError("الرجاء إكمال جميع الحقول المطلوبة");
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.post("/api/orders/", {
        clientFirstName: data.firstName,
        clientLastName: data.lastName,
        clientPhoneNumber: data.phone,
        products: [{ id: selectedProductId, quantity: quantity }],
        addressDetails: data.addressDetails,
        wilaya: data.wilaya,
        origin: "web",
      });

      Swal.fire({
        position: "top",
        icon: "success",
        title: "تم تأكيد الطلبية",
        text: "سنتواصل معك قريبا",
        showConfirmButton: false,
        timer: 1250,
      });

      setSubmitting(false);
      setError("");
      orderPlaced();
      navigate("/", { replace: true });
    } catch (error) {
      setError("حدث خطأ ما، جرب مرة أخرى");
      console.log(error);
      setSubmitting(false);
    }
  };

  return (
    <NavContainer>
      <div className="bg-[#192d42] p-4 sm:p-8 sm:flex items-center justify-around">
        <div className="flex justify-center mb-8 sm:mb-0">
          <img className="w-44" src="./../static/logo_white.png" alt="" />
        </div>

        <div className="text-center text-white">
          <p className="font-bold text-2xl mb-4">مصاحف نقرة</p>
          <p className="mb-3">
            المصاحف المتوفرة لدينا بمختلف الأحجام والألوان. نوفر كل ما تحتاجه من
            مصاحف بجودة عالية
          </p>
          <p className="mb-3">
            شركات التوصيل المتعاقدة معنا تقوم بتوصيل طلبيتك إلى باب بيتك...
          </p>
          <p className="mb-3">الدفع يكون عند استلامك للطلبية...</p>
        </div>
      </div>

      <div className="flex flex-col-reverse md:flex-row gap-2 py-6 px-4 mt-4">
        <div className="flex-1 px-4">
          <div className="bg-blue-100 rounded-lg p-4">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <LoadingIcon />
              </div>
            ) : (
              <form onSubmit={handleSubmit} action="" method="post">
                <p className="font-bold text-center mb-2">
                  للطلب إملأ الاستمارة
                </p>

                {/* Error message box */}
                {error && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <p className="font-bold">تنبيه!</p>
                    <p>{error}</p>
                  </div>
                )}

                <div className="">
                  <label htmlFor="name">الإسم الأول</label>
                  <input
                    className={`border-solid block mt-1 py-1 px-2 rounded-xl w-full ${
                      fieldErrors.firstName ? "border-red-500" : ""
                    }`}
                    type="text"
                    name="name"
                    placeholder="الإسم الأول"
                    value={data.firstName}
                    onChange={(e) =>
                      setData((prev) => {
                        return { ...prev, firstName: e.target.value };
                      })
                    }
                  />
                  {fieldErrors.firstName && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.firstName}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <label htmlFor="lastname">اللقب</label>
                  <input
                    className={`border-solid block mt-1 py-1 px-2 rounded-xl w-full ${
                      fieldErrors.lastName ? "border-red-500" : ""
                    }`}
                    type="text"
                    name="lastname"
                    placeholder="اللقب"
                    value={data.lastName}
                    onChange={(e) =>
                      setData((prev) => {
                        return { ...prev, lastName: e.target.value };
                      })
                    }
                  />
                  {fieldErrors.lastName && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.lastName}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <label htmlFor="phone">رقم الهاتف</label>
                  <input
                    className={`border-solid block mt-1 py-1 px-2 rounded-xl w-full ${
                      fieldErrors.phone ? "border-red-500" : ""
                    }`}
                    type="text"
                    name="phone"
                    placeholder="رقم الهاتف"
                    value={data.phone}
                    onChange={(e) =>
                      setData((prev) => {
                        return { ...prev, phone: e.target.value };
                      })
                    }
                  />
                  {fieldErrors.phone && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.phone}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <label htmlFor="wilaya">
                    الولاية (سعر التوصيل حسب الولاية)
                  </label>
                  <select
                    className={`border-solid block mt-1 py-1 px-2 rounded-xl w-full ${
                      fieldErrors.wilaya ? "border-red-500" : ""
                    }`}
                    name="wilaya"
                    id=""
                    value={data.wilaya}
                    onChange={handleWilayaChange}
                  >
                    <option>إختر الولاية</option>
                    {WILAYAS.map((w, i) => {
                      return (
                        <option key={i} value={w.name}>
                          {w.id}-{w.name}
                        </option>
                      );
                    })}
                  </select>
                  {fieldErrors.wilaya && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.wilaya}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <label htmlFor="addressDetails">الدائرة</label>
                  <input
                    className={`border-solid block mt-1 py-1 px-2 rounded-xl w-full ${
                      fieldErrors.addressDetails ? "border-red-500" : ""
                    }`}
                    type="text"
                    name="addressDetails"
                    placeholder="الدائرة"
                    value={data.addressDetails}
                    onChange={(e) =>
                      setData((prev) => {
                        return { ...prev, addressDetails: e.target.value };
                      })
                    }
                  />
                  {fieldErrors.addressDetails && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldErrors.addressDetails}
                    </p>
                  )}
                </div>

                <div className="bg-blue-300 text-blue-900 font-bold mt-4 rounded-xl p-2">
                  <p>سعر المصحف: {product?.price || 0}دج</p>
                  <p className="mt-2">سعر التوصيل: {deliveryCharge}دج</p>
                  <p className="mt-2">
                    السعر الإجمالي: {calculateTotalPrice()}دج
                  </p>
                </div>
                <button
                  className="text-center flex gap-2 justify-center w-full bg-green-500 rounded-xl mt-4 p-2 text-white hover:bg-green-600"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? <LoadingIcon /> : null}
                  الطلب
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="flex-1 mb-8 md:mb-0">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingIcon />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <h1 className="font-bold text-xl text-center">
                {product?.name || ""}
              </h1>
              <p className="text-center">{product?.details || ""}</p>
              <img
                src={product?.img || ""}
                alt="مصحف"
                className="w-[200px] h-fit rounded-xl drop-shadow-sm"
              />

              {/* Quantity Selector */}
              <div className="flex items-center justify-center mt-2 md:mt-0">
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700"
                    onClick={() => {
                      const newQuantity = Math.max(1, quantity - 1);
                      setQuantity(newQuantity);
                    }}
                  >
                    −
                  </button>

                  <input
                    className="w-16 h-12 text-center border-l border-r border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    type="number"
                    name="quantity"
                    min="1"
                    value={quantity}
                    onChange={handleQuantityChange}
                  />

                  <button
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700"
                    onClick={() => {
                      const newQuantity = quantity + 1;
                      setQuantity(newQuantity);
                    }}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Size Selection */}
              <div className="w-full max-w-md mt-6">
                <h2 className="font-bold text-lg text-center mb-3">
                  اختر الحجم
                </h2>
                <div className="flex flex-wrap justify-center gap-4">
                  {masahifSizesWithProducts[0].map((sizeOption, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedSizeIndex(index)}
                      className={`py-2 px-4 rounded-lg border-2 ${
                        selectedSizeIndex === index
                          ? "border-blue-600 bg-blue-100"
                          : "border-gray-300 hover:border-blue-400"
                      }`}
                    >
                      <div className="text-center">
                        <p>{sizeOption.size}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Product Selection */}
              <div className="w-full max-w-full mt-6">
                <h2 className="font-bold text-lg text-center mb-3">
                  اختر المنتج
                </h2>
                <div className="flex flex-wrap justify-center gap-4 max-h-64 overflow-y-auto">
                  {masahifSizesWithProducts[0][selectedSizeIndex]?.ids.map(
                    (productItem) => (
                      <button
                        key={productItem.id}
                        onClick={() => setSelectedProductId(productItem.id)}
                        className={`py-2 px-4 rounded-lg border-2 ${
                          selectedProductId === productItem.id
                            ? "border-blue-600 bg-blue-100"
                            : "border-gray-300 hover:border-blue-400"
                        }`}
                      >
                        <div className="text-center w-40">
                          <p className="text-sm truncate">
                            {productItem.name
                              .replace("مصحف ورش", "")
                              .replace(/\d{2}-\d{2}/g, "")
                              .trim()}
                          </p>
                        </div>
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </NavContainer>
  );
};

export default MasahifPage;
